import React, { useEffect, useState } from 'react'
import qs from 'query-string'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from '@apollo/react-hooks'
import { useLocation } from 'react-router-dom'
import ReactSVG from 'react-svg'

import Form from '../../../../../components/forms/layout-form'
import Row from '../../../../../components/forms/layout-row'
import Col from '../../../../../components/forms/layout-column'
import Input from '../../../../../components/form-fields/input'
import Button from '../../../../../components/button'
import { FORM_MESSAGES } from '../../../../../constants'
import RENAME_NAMED_LIST from '../../../../../../graphql/mutations/rename-named-list.gql'
import arrowLeft from '../../../../../../images/arrow-left-light.svg'
import { getGQLErrorMsg } from '../../../../../lib/apollo'
import styles from './rename-list-modal.css'

/**
 * Rename list modal
 *
 * Key features:
 * - Reads list context (`listName`, `listId`) from modal query params.
 * - Submits rename mutation and closes the modal on success.
 */
const RenameListModal = ({ onRequestClose }) => {
  const location = useLocation()
  const parsedSearch = qs.parse(location && location.search)
  const { listName, listId } = parsedSearch
  const [submitError, setSubmitError] = useState('')
  const [renameList] = useMutation(RENAME_NAMED_LIST)
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      name: listName || ''
    }
  })
  const isSaveDisabled = !listId || !listName

  useEffect(() => {
    setValue('name', listName || '')
  }, [listName, setValue])

  const onSubmit = async (data) => {
    try {
      await renameList({
        variables: {
          id: listId,
          name: data.name.trim()
        }
      })
      setSubmitError('')
      onRequestClose()
    } catch (err) {
      setSubmitError(getGQLErrorMsg(err) || 'An error occurred while renaming the list. Please try again.')
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <button
          type="button"
          onClick={onRequestClose}
          className={styles.iconButtonFrame}
        >
          <div className={styles.iconButton}>
            <ReactSVG
              src={arrowLeft}
              beforeInjection={(svg) => {
                svg.setAttribute('class', styles.icon)
              }}
            />
          </div>
        </button>
        <span className={styles.header}>
          Edit List Name
        </span>
      </div>

      <Form
        formClassName={styles.form}
        handleSubmit={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
      >
        <Row>
          <Col>
            <Controller
              name="name"
              control={control}
              rules={{
                required: FORM_MESSAGES.required,
                // Reject values that are only whitespace.
                validate: value => {
                  return value.trim().length > 0 || 'Please enter a valid list name'
                }
              }}
              render={({ field }) => (
                <Input
                  label="List Name"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    if (submitError) {
                      setSubmitError('')
                    }
                  }}
                  error={errors?.name?.message || submitError}
                  data-e2e="rename-list-form"
                />
              )}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <Button
              wrapperClassName={styles.buttonWrapper}
              text="Save"
              className={styles.button}
              disabled={isSubmitting || isSaveDisabled}
            />
          </Col>
        </Row>
      </Form>
    </div>
  )
}

export default RenameListModal

RenameListModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired
}
